import type { ReactNode } from "react";
import { motion } from "motion/react";
import { cn } from "./cn";
import { Illustration, type IllustrationName } from "./Illustration";
import { Mark } from "./Mark";
import { rise, staggerParent } from "./motion";

export type EmptyStateProps = {
  title: ReactNode;
  text?: ReactNode;
  action?: ReactNode;
  /** A scene in place of the mark tile, for a real empty state. Errors keep the tile. */
  art?: IllustrationName;
  compact?: boolean;
  className?: string;
};

/** What is happening and what to do next, centred, with a scene or the mark in a tile above. */
export function EmptyState({ title, text, action, art, compact, className }: EmptyStateProps) {
  return (
    <motion.div
      className={cn("flex flex-col items-center text-center", compact ? "px-5 py-10" : "px-6 py-16", className)}
      variants={staggerParent(0.05, 0.08)}
      initial="hidden"
      animate="show"
    >
      {art ? (
        <Illustration name={art} className={compact ? "mb-4 w-[120px]" : "mb-5 w-40"} />
      ) : (
        <motion.span
          className={cn("grid place-items-center rounded-xl bg-primary-soft", compact ? "mb-4 size-12" : "mb-5 size-16")}
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 380, damping: 18 }}
        >
          <Mark size={compact ? 24 : 32} pop />
        </motion.span>
      )}
      <motion.h2 variants={rise} className={cn("font-display text-balance text-ink", compact ? "text-lg" : "text-[24px] leading-[1.15]")}>
        {title}
      </motion.h2>
      {text ? (
        <motion.p variants={rise} className="mt-2 max-w-[40ch] text-[15px] text-muted text-pretty">
          {text}
        </motion.p>
      ) : null}
      {action ? (
        <motion.div variants={rise} className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {action}
        </motion.div>
      ) : null}
    </motion.div>
  );
}
